// Subscription memory — Context-backed Set of streamer labels the user
// actually subscribes to. Used to bubble those games to the top of each
// date group and to decorate matching streamer badges with a checkmark.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "tracker.subscriptions.v1";

function readSubs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((s) => typeof s === "string" && s.length > 0);
  } catch { return []; }
}

const Ctx = createContext(null);

export function SubscriptionsProvider({ children }) {
  const [subs, setSubs] = useState(readSubs);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(subs)); } catch {}
  }, [subs]);

  const toggle = useCallback((label) => {
    if (!label) return;
    setSubs((prev) => (
      prev.includes(label) ? prev.filter((s) => s !== label) : [...prev, label]
    ));
  }, []);

  const has = useCallback((label) => subs.includes(label), [subs]);
  const clear = useCallback(() => setSubs([]), []);

  const value = useMemo(() => ({
    subscriptions: subs,
    toggle,
    has,
    clear,
    hasAny: subs.length > 0,
    count: subs.length,
  }), [subs, toggle, has, clear]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSubscriptions() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSubscriptions must be used inside <SubscriptionsProvider>");
  return v;
}

/** Does this game stream on at least one of the user's subscribed services? */
export function gameOnSubscribed(game, subsSet) {
  if (!subsSet || subsSet.size === 0) return false;
  const platforms = (game?.platforms && game.platforms.length > 0)
    ? game.platforms
    : (game?.platform ? [game.platform] : []);
  // The caller passes a Set of normalized labels so we match against
  // the platform label (not raw string). Comparison done lazily by caller.
  return platforms.some((p) => subsSet.has(p));
}
